import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowDown } from "lucide-react";

const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

/** Smallest package that covers the drink count. */
export const packageFor = (packages, drinks) =>
  (packages || [])
    .filter((p) => p.price_amount > 0 && drinks <= p.drink_count)
    .sort((a, b) => a.drink_count - b.drink_count)[0] || null;

export const quoteFor = (cfg, { drinks, serviceId, alani }) => {
  const d = parseInt(drinks, 10);
  if (!d) return null;
  const service = (cfg.services || []).find((s) => s.id === serviceId) || null;
  const pack = packageFor(cfg.packages, d);
  const alaniAmount = (cfg.upgrades || []).find((u) => u.amount > 0)?.amount || 0;
  const alaniQty = Math.min(parseInt(alani, 10) || 0, d);
  const upgrade = alaniQty * alaniAmount;
  if (!pack) return { custom: true, service, alaniQty, upgrade };
  const belowMin = service && d < service.min_drinks ? service : null;
  const fee = service?.fee || 0;
  const total = pack.price_amount + fee + upgrade;
  return {
    pack, service, fee, upgrade, alaniQty, total, belowMin,
    deposit: (total * (cfg.deposit_pct || 50)) / 100,
  };
};

export const InstantQuote = ({ packages: cfg, onApply }) => {
  const services = cfg?.services || [];
  const [drinks, setDrinks] = useState("150");
  const [serviceId, setServiceId] = useState(services[0]?.id || "");
  const [alani, setAlani] = useState("0");
  const quote = useMemo(
    () => (cfg ? quoteFor(cfg, { drinks, serviceId, alani }) : null),
    [cfg, drinks, serviceId, alani]
  );
  if (!cfg) return null;

  const row = (label, value, testId) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-3">
      <span className="mono text-[10px] leading-relaxed tracking-widest text-muted-foreground">
        {label}
      </span>
      <span data-testid={testId} className="mono text-sm text-white">{value}</span>
    </div>
  );

  return (
    <div data-testid="instant-quote" className="border border-primary/60 bg-[#141414] p-6 md:p-8">
      <h3 className="display text-2xl uppercase leading-none md:text-3xl">{cfg.quote_title}</h3>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="mono text-[11px] tracking-widest text-muted-foreground">
            HOW MANY DRINKS?
          </Label>
          <Input
            data-testid="quote-drinks"
            inputMode="numeric"
            value={drinks}
            onChange={(e) => setDrinks(e.target.value.replace(/[^0-9]/g, ""))}
            className="mt-1 rounded-sm bg-[#0A0A0A]"
          />
        </div>
        <div>
          <Label className="mono text-[11px] tracking-widest text-muted-foreground">
            DIRTY ALANI UPGRADES (+$1 EACH)
          </Label>
          <Input
            data-testid="quote-alani"
            inputMode="numeric"
            value={alani}
            onChange={(e) => setAlani(e.target.value.replace(/[^0-9]/g, ""))}
            className="mt-1 rounded-sm bg-[#0A0A0A]"
          />
        </div>
      </div>

      <div className="mt-5">
        <Label className="mono text-[11px] tracking-widest text-muted-foreground">
          SERVICE OPTION
        </Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {services.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`quote-service-${s.id}`}
              onClick={() => setServiceId(s.id)}
              className={`mono rounded-sm border px-3 py-2.5 text-[10px] tracking-widest transition-colors ${
                s.id === serviceId
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {s.name.toUpperCase()} · {s.price_label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {!quote ? (
        <p className="mono mt-6 text-[11px] tracking-widest text-muted-foreground">
          ENTER HOW MANY DRINKS YOU NEED.
        </p>
      ) : quote.custom ? (
        <div data-testid="quote-custom" className="mt-6">
          <div className="display text-3xl uppercase leading-none text-primary md:text-4xl">
            Custom quote
          </div>
          <p className="mt-3 text-base text-muted-foreground">
            That's a Full Smash. Tell us the drink count below and we'll price it properly.
          </p>
        </div>
      ) : (
        <div className="mt-6">
          {row(`${quote.pack.name.toUpperCase()} · ${quote.pack.drinks.toUpperCase()}`,
            money(quote.pack.price_amount), "quote-line-package")}
          {quote.service
            ? row(`${quote.service.name.toUpperCase()} (STARTING AT)`,
                quote.fee ? money(quote.fee) : "Free", "quote-line-service")
            : null}
          {quote.upgrade
            ? row(`DIRTY ALANI × ${quote.alaniQty}`, money(quote.upgrade), "quote-line-alani")
            : null}
          <div className="flex items-baseline justify-between gap-4 pt-5">
            <span className="mono text-[11px] tracking-widest text-primary">ESTIMATED TOTAL</span>
            <span data-testid="quote-total" className="display text-4xl leading-none text-primary md:text-5xl">
              {money(quote.total)}
            </span>
          </div>
          <div className="mono mt-3 text-right text-[11px] tracking-widest text-muted-foreground">
            <span data-testid="quote-deposit">{money(quote.deposit)}</span> DEPOSIT (
            {cfg.deposit_pct || 50}%) TO RESERVE YOUR EVENT
          </div>
          {quote.belowMin ? (
            <p data-testid="quote-min-note" className="mono mt-4 text-[10px] leading-relaxed tracking-widest text-primary">
              {quote.belowMin.name.toUpperCase()} HAS A {quote.belowMin.min_drinks} DRINK MINIMUM —
              WE'LL CONFIRM WHAT WORKS FOR YOUR EVENT.
            </p>
          ) : null}
        </div>
      )}

      <button
        type="button"
        data-testid="quote-apply"
        onClick={() =>
          onApply({
            drink_count: drinks,
            package: quote?.pack?.name || "The Full Smash",
            service_option: quote?.service
              ? `${quote.service.name} (${quote.service.price_label})`
              : "",
            alani_count: alani,
          })
        }
        className="display mt-7 inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-lg uppercase text-primary-foreground transition-colors hover:bg-[#E6C800]"
      >
        Use this in my request
        <ArrowDown size={16} />
      </button>

      {cfg.quote_note ? (
        <p className="mono mt-5 text-[10px] leading-relaxed tracking-widest text-muted-foreground">
          {cfg.quote_note}
        </p>
      ) : null}
    </div>
  );
};
